import axios from 'axios';

import { getTokenFromCookie, getDecodedToken } from '../utils/tokenHelper';

// Interface for job location
export interface JobLocation {
  jobId: number;
  country: string;
  region: string;
  postalCode: string;
  city: string;
  externalLocationId: string | null;
}

// Interface for division data from API
export interface DivisionData {
  id: number;
  companyId: number;
  name: string;
  default: boolean;
  active: boolean;
}

// Interface for user/recruiter data from API
export interface UserData {
  id: number;
  companyId: number;
  partnerId: number;
  userType: string;
  active: boolean;
  contactInfo: {
    firstName: string;
    lastName: string;
    title: string | null;
    emailAddress: string;
    phoneNumber: string | null;
  };
}

// Interface for job data from API
export interface JobData {
  id: number;
  recruiterId: number;
  divisionId: number;
  companyId: number;
  partnerId: number;
  requisitionName: string;
  title: string;
  description: string;
  companyName: string;
  applyUrl: string;
  requirements: string | null;
  salaryLow: number | null;
  salaryHigh: number | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactExtension: string | null;
  anonymousPosting: boolean;
  entryLevel: boolean;
  jobFunction: string | null;
  experience: string | null;
  industry: string | null;
  jobType: string | null;
  duration: string | null;
  travel: string | null;
  education: string | null;
  salaryType: string | null;
  jobOrigin: string;
  status: string;
  created: string;
  updated: string;
  locations: JobLocation[];
  advertisingStatus?: string;
  easyApplyEnabled?: boolean;
}

// Using relative URL path that will go through the proxy
const API_PATH = '/api/v1';

//TO DO: Get company ID from token
// const DEFAULT_COMPANY_ID = 6444;

const decodedToken = getDecodedToken();
const company_id = decodedToken?.companyId ?? 17691;

/**
 * Helper function to fetch data using the React proxy
 * @param endpoint API endpoint path (without base URL)
 * @param options Optional headers for the API call
 * @returns Promise with API response data
 */
const fetchWithFallback = async <T>(endpoint: string, options?: { headers?: any }): Promise<T> => {
  try {
    const proxyUrl = `${API_PATH}${endpoint}`;
    
    const response = await axios.get(proxyUrl, options);
    return response.data;
  } catch (error) {
    console.error(`❌ API call failed for ${endpoint}:`, error);
    throw error;
  }
};

export interface PaginatedJobsResponse {
  jobs: JobData[];
  page: number;
  count: number;
  total: number;
  totalPages: number;
}

export interface JobSearchResponse {
  jobs: JobData[];
  total: number;
}

export const JobsService = {
  getJobs: async (companyId: number = company_id): Promise<JobData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/jobs`;
    const response = await fetchWithFallback<PaginatedJobsResponse | JobData[]>(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (Array.isArray(response)) return response;
    return response.jobs;
  },

  getJobsPaginated: async (
    companyId: number = company_id,
    page: number = 0,
    count: number = 50,
    skipEnrichment: boolean = false
  ): Promise<PaginatedJobsResponse> => {
    const token = getTokenFromCookie();
    const enrichParam = skipEnrichment ? '&skipEnrichment=true' : '';
    const endpoint = `/company/${companyId}/jobs?page=${page}&count=${count}${enrichParam}`;
    return fetchWithFallback<PaginatedJobsResponse>(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  searchJobs: async (
    keyword: string,
    page: number = 0,
    rows: number = 50,
    // VI-375: optional field scope ('jobTitle' | 'jobId' | 'reqId'). Omitting it
    // keeps the legacy broad (title + requisition) search for existing callers.
    searchBy?: string
  ): Promise<JobSearchResponse> => {
    const token = getTokenFromCookie();
    const params = new URLSearchParams({
      keyword,
      page: page.toString(),
      rows: rows.toString(),
    });
    if (searchBy) {
      params.append('searchBy', searchBy);
    }
    const endpoint = `/job/search?${params.toString()}`;
    return fetchWithFallback<JobSearchResponse>(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  getJobById: async (jobId: number): Promise<JobData> => {
    const token = getTokenFromCookie();
    const endpoint = `/job/${jobId}`;
    return fetchWithFallback<JobData>(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  // Get all divisions for a company
  getDivisions: async (companyId: number = company_id): Promise<DivisionData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/division`;
    return fetchWithFallback<DivisionData[]>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Get all users/recruiters for a company
  getUsers: async (companyId: number = company_id): Promise<UserData[]> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/${companyId}/user`;
    return fetchWithFallback<UserData[]>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  getInterviewByQuestionnaireIdAndJobId: async (questionnaireId: string, jobId: number): Promise<any> => {
    const token = getTokenFromCookie();
    const endpoint = `/interview/${questionnaireId}/${jobId}`;
    return fetchWithFallback<any>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  // Relationship manager email for the current user's company (Easy Apply tooltip contact)
  getRelationshipManagerEmail: async (): Promise<string | null> => {
    const token = getTokenFromCookie();
    const endpoint = `/company/relationship-manager-email`;
    const response = await fetchWithFallback<{ email: string | null }>(endpoint, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.email;
  }
};

export default JobsService; 